import { useEffect, useState, useMemo, type ReactNode } from "react";
import { onAuthStateChanged, signInAnonymously, type User } from "firebase/auth";
import { AuthContext } from "../contexts/AuthContext";
import { auth } from "../firebase/config";
import { useTranslation } from "react-i18next";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();

    // Anonymous user from Firestore
    const [userFirebase, setUserFirebase] = useState<User | null>(null);

    const [error, setError] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        // Listener for authentication state
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                // User already authenticated (anonymous or not)
                setUserFirebase(currentUser);
                setLoading(false);
            } else {
                // No user, perform anonymous login
                try {
                    const credential = await signInAnonymously(auth);
                    setUserFirebase(credential.user);
                } catch (error) {
                    setError(true);
                    console.error(error);
                } finally {
                    setLoading(false);
                }
            }
        });

        return () => unsubscribe();
    }, []);

    const value = useMemo(() => ({
        userFirebase,
        loading,
        error,
    }), [
        userFirebase,
        loading,
        error,
    ]);

    // Show loader until authentication is complete
    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-4">
                <span className="loading loading-dots loading-xl"></span>
                <p className="text-xl">{t('loading.tenant')}</p>
            </div>
        );
    }

    // If there is an authentication error
    if (error) {
        return (
            <div className="flex flex-col justify-center items-center h-screen p-8 text-center gap-4">
                <h1 className="text-2xl font-bold">{t('errors.authProviderTitle')}</h1>
                <p>{t('errors.authProvider')}</p>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
